import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TableSortLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/material/styles";
import { Box, IconButton, Spinner, Text } from "@origin-digital/ods-core";
import { TotalIcon, UnreadMessageIcon } from "./Icons";
import {
  ProjectSortByEnum,
  ProjectsTableRow,
  RequestSummary,
  Status,
} from "./types";
import { IconGas } from "@origin-digital/ods-icons";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { SortDirectionEnum } from "../Premises/types";
import { mapProjectsTableRowKeyToSortByEnum } from "./helpers";
import {
  RequestStatusCompletedIcon,
  RequestStatusDraftIcon,
  RequestStatusFinishedIcon,
  RequestStatusInProgressIcon,
} from "../Premises/Icons";
import { IconElectricityLocal } from "../Icons";
import { useTheme } from "src/hooks";
import { useFeedContext } from "src/context/Feed";
import { useProjectNavigation } from "./hooks";

const StyledTableCell = styled(TableCell)(() => ({
  textAlign: "left",
  verticalAlign: "middle",
}));

const StyledTableHeadCell = styled(StyledTableCell)(() => ({
  backgroundColor: "#f8f8f8",
}));

const StyledTableRow = styled(TableRow)(() => ({
  height: 64,
}));

const StatusTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  textAlign: "center",
}));

const StatusIcon = ({
  status,
  newMessage,
}: {
  status: Status;
  newMessage: number;
}) => {
  switch (status) {
    case Status.NEW_MESSAGE:
      return (
        <Box display={"flex"} alignItems={"center"}>
          <UnreadMessageIcon />
          <Box
            paddingLeft={{
              lg: "small",
            }}
          >
            <Text variant="body-small" tone="critical" weight="medium">
              {newMessage}
            </Text>
          </Box>
        </Box>
      );
    case Status.BOOK_CLOSURE:
      return <RequestStatusCompletedIcon variant="solid" />;
    case Status.DRAFT:
      return <RequestStatusDraftIcon />;
    case Status.LODGED:
      return <RequestStatusInProgressIcon />;
    case Status.FINISHED:
      return <RequestStatusFinishedIcon variant="solid" />;
    case Status.NONE:
      return null;
    default:
      return null;
  }
};

type ColumnConfig = {
  id: keyof ProjectsTableRow;
  label: string;
  width?: string;
  disableSorting?: boolean;
};

const columns: ColumnConfig[] = [
  { id: "name", label: "Project name", width: "40%" },
  { id: "id", label: "ID", disableSorting: true },
  { id: "drafts", label: "Drafts", disableSorting: true },
  { id: "submitted", label: "Lodged", disableSorting: true },
  { id: "book_closure", label: "Booked closure", disableSorting: true },
  { id: "finished", label: "Finished", disableSorting: true },
  { id: "status", label: "Status" },
];

const chartOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false, // This will hide the legend
    },
    tooltip: {
      enabled: true, // This will turn tooltips on
    },
  },
};

export const ProjectsTable: React.FC = () => {
  const {
    loading,
    rows,
    sortConfig,
    setSortConfig,
    scrollableProps,
    doFetch,
    setPage,
    setScrollPosition,
  } = useFeedContext();

  const theme = useTheme();
  const handleSort = (key: keyof ProjectsTableRow) => {
    const isAsc =
      sortConfig &&
      sortConfig.key === mapProjectsTableRowKeyToSortByEnum(key) &&
      sortConfig.direction === SortDirectionEnum.ASC;
    const sortBy =
      mapProjectsTableRowKeyToSortByEnum(key) ?? ProjectSortByEnum.NAME;
    const sortDirection = isAsc
      ? SortDirectionEnum.DESC
      : SortDirectionEnum.ASC;
    setSortConfig({
      key: sortBy,
      direction: sortDirection,
    });
    setPage(0);
    setScrollPosition(0);
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortBy,
        direction: sortDirection,
      },
    });
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        maxHeight: 700,
        overflow: "auto",
      }}
      onScroll={scrollableProps.onContainerScroll}
      ref={scrollableProps.containerRef}
    >
      <Table
        sx={{ minWidth: 650 }}
        size="small"
        aria-label="projects table"
        stickyHeader
      >
        <TableHead sx={{ backgroundColor: "#F1F1F1" }}>
          <StyledTableRow>
            {columns.map((column) => {
              const isCurrentCol =
                sortConfig?.key ===
                mapProjectsTableRowKeyToSortByEnum(column.id);
              return (
                <StyledTableHeadCell
                  key={column.id}
                  sx={column.width ? { width: column.width } : {}}
                >
                  {column.disableSorting ? (
                    <Text variant="body-small" weight="medium">
                      {column.label}
                    </Text>
                  ) : (
                    <TableSortLabel
                      active={isCurrentCol}
                      direction={
                        isCurrentCol
                          ? (sortConfig.direction.toLowerCase() as
                              | "desc"
                              | "asc")
                          : "asc"
                      }
                      onClick={() => handleSort(column.id)}
                      sx={{
                        "&.Mui-active .MuiTableSortLabel-icon": {
                          color: theme.colors.critical,
                        },
                      }}
                    >
                      <Text
                        variant="body-small"
                        tone={isCurrentCol ? "critical" : undefined}
                        weight="medium"
                      >
                        {column.label}
                      </Text>
                    </TableSortLabel>
                  )}
                </StyledTableHeadCell>
              );
            })}
            <StyledTableHeadCell align="right" />
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows && rows.map((row) => <Row key={row.id + row.name} row={row} />)}
        </TableBody>
      </Table>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY="medium"
        >
          <Spinner />
        </Box>
      )}
    </TableContainer>
  );
};

const statusTableRow: {
  label: string;
  icon: React.ReactNode;
  id: keyof RequestSummary;
}[] = [
  { label: "Drafts", icon: <RequestStatusDraftIcon />, id: "drafts" },
  { label: "Lodged", icon: <RequestStatusInProgressIcon />, id: "lodged" },
  {
    label: "Book Closure",
    icon: <RequestStatusCompletedIcon variant="solid" />,
    id: "book_closure",
  },
  {
    label: "Finished",
    icon: <RequestStatusFinishedIcon variant="solid" />,
    id: "finished",
  },
  { label: "Total connections", icon: <TotalIcon />, id: "total" },
];

const StatusTable: React.FC<{ row: ProjectsTableRow }> = ({ row }) => {
  return (
    <Table aria-label="customized table" sx={{ maxWidth: "200px" }}>
      <TableBody>
        <TableRow>
          <StatusTableCell component="th" scope="row">
            <Text variant="body" component="div" weight="medium">
              Connection Status
            </Text>
          </StatusTableCell>
          <StatusTableCell>
            <IconElectricityLocal color="neutral" />
          </StatusTableCell>
          <StatusTableCell>
            <IconGas color="neutral" />
          </StatusTableCell>
        </TableRow>
        {statusTableRow.map((status, index) => (
          <TableRow key={index}>
            <StatusTableCell
              component="th"
              scope="row"
              style={{ display: "flex", alignItems: "center", width: "200px" }}
            >
              {status.icon}
              <Box paddingLeft={"small"}>
                <Text variant="body" component="div" weight="medium">
                  {status.label}
                </Text>
              </Box>
            </StatusTableCell>
            <StatusTableCell>
              {row.projectRaw.electricity_request_summary[status.id]}
            </StatusTableCell>
            <StatusTableCell>
              {row.projectRaw.gas_request_summary[status.id]}
            </StatusTableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const Row = ({ row }: { row: ProjectsTableRow }) => {
  const [open, setOpen] = useState(false);
  const { goToPremisesByProject } = useProjectNavigation();

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const chartData = {
    labels: ["Drafts", "Lodged", "Connected", "Closed"],
    datasets: [
      {
        label: "# of connections",
        data: [row.drafts, row.lodged, row.book_closure, row.finished],
        backgroundColor: ["#FA4616", "#0A5FD2", "#C34789", "#008906"],
        borderColor: ["white"],
        borderWidth: 1,
        cutout: "70%",
      },
    ],
  };

  return (
    <>
      <StyledTableRow
        onClick={() => goToPremisesByProject(row)}
        sx={{
          cursor: "pointer",
        }}
      >
        <StyledTableCell component="th" scope="row">
          {row.name}
        </StyledTableCell>
        <StyledTableCell align="right">{row.id}</StyledTableCell>
        <StyledTableCell align="right">{row.drafts}</StyledTableCell>
        <StyledTableCell align="right">{row.submitted}</StyledTableCell>
        <StyledTableCell align="right">{row.book_closure}</StyledTableCell>
        <StyledTableCell align="right">{row.finished}</StyledTableCell>
        <StyledTableCell align="right">
          <StatusIcon status={row.status} newMessage={row.stalled} />
        </StyledTableCell>
        <StyledTableCell padding="normal">
          <IconButton
            color="secondaryB"
            aria-label="collpase btn"
            onClick={(e) => toggleCollapse(e)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {/* @ts-expect-error: ODS Box doesn't accept space-around but works */}
            <Box padding={"large"} display="flex" justifyContent="space-around">
              <StatusTable row={row} />
              <Box padding={"large"} style={{ maxWidth: "500px" }}>
                <Doughnut data={chartData} options={chartOptions} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
